import {
  BodyTextStyle,
  resetButtonStyle,
  ToggleArrow,
  useNameAndId
} from '@kvdbil/components';
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import Link from '~/App/shared/components/Link';
import { useKeypressEffect } from '~/App/shared/hooks/keyPressHook';
import { useOutsideClick } from '~/App/shared/hooks/outsideClickHook';
import { BreadcrumbItem } from '../helpers';

const Wrapper = styled.div`
  position: relative;
  display: flex;
`;

const crumbStyle = css`
  ${BodyTextStyle}
  text-decoration: none;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.info.dark};
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
`;

const Trigger = styled.button.attrs({ type: 'button' })`
  ${resetButtonStyle};
  ${crumbStyle};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;

  &:focus-visible {
    outline: revert;
  }
`;

const StaticCrumbLink = styled(Link)`
  ${crumbStyle};
`;

const StaticCrumbText = styled.span`
  ${crumbStyle};
`;

const Menu = styled.ul`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  margin: 0.25rem 0 0;
  padding: 0;
  min-width: 14rem;
  max-width: 80vw;
  list-style: none;
  text-align: left;
  background: ${({ theme }) => theme.colors.background.light};
  box-shadow: ${({ theme }) => theme.elevations.elevation2};
`;

const MenuRow = styled.li`
  margin: 0;
  padding: 0.75rem 1rem;
  text-indent: 0;
  list-style-type: none;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.grayscaleToned.light6};
  }

  a {
    ${BodyTextStyle}
    display: block;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.info.dark};
  }
`;

const CurrentPage = styled.span`
  ${BodyTextStyle}
  display: block;
  color: ${({ theme }) => theme.colors.text.dark};
`;

interface Props {
  trigger: BreadcrumbItem;
  items: BreadcrumbItem[];
}

const BreadcrumbsDropdown = ({ trigger, items }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { id: menuId } = useNameAndId('breadcrumb-dropdown');

  const close = useCallback(() => setIsOpen(false), []);
  const wrapperRef = useOutsideClick(close);
  useKeypressEffect('Escape', close, {});

  if (items.length === 0) {
    return (
      <nav aria-label="Breadcrumb">
        {trigger.to ? (
          <StaticCrumbLink to={trigger.to}>{trigger.label}</StaticCrumbLink>
        ) : (
          <StaticCrumbText>{trigger.label}</StaticCrumbText>
        )}
      </nav>
    );
  }

  return (
    <nav aria-label="Breadcrumb">
      <Wrapper ref={wrapperRef}>
        <Trigger
          aria-expanded={isOpen}
          aria-controls={menuId}
          onClick={() => setIsOpen(open => !open)}
        >
          {trigger.label}
          <ToggleArrow isExpanded={isOpen} />
        </Trigger>

        {isOpen && (
          <Menu id={menuId}>
            {items.map(item => (
              <MenuRow key={item.key}>
                {item.to ? (
                  <Link to={item.to} onClick={close}>
                    {item.label}
                  </Link>
                ) : (
                  <CurrentPage aria-current="page">{item.label}</CurrentPage>
                )}
              </MenuRow>
            ))}
          </Menu>
        )}
      </Wrapper>
    </nav>
  );
};

export default BreadcrumbsDropdown;
